
// Chakra imports
import { Box, Button, Grid, GridItem, Modal, SimpleGrid, Spinner, Stack, useToast } from "@chakra-ui/react";
import React, { useContext, useMemo, useState } from "react";
import { useContent } from "hooks/useContent";
import { useContentUsage } from "hooks/useContentUsage";
import MultiSelectFilter from "components/filter/MultiSelectFilter";
import { resolutionCounts, resolutionKey } from "lib/resolution";
import { AuthContext } from "contexts/AuthContext";
import ContentUploadModal from "views/modal/content-upload";
import { ActionDrawer } from "components/drawer";
import { ConfirmationDialog } from "components/confirmation/confirmation";
import DataTableNew from "components/table/DataTableNew";
import { columnsContent } from "./columns/content";
import { removeContent } from "lib/firebase";
import ContentGrid from "./ContentGrid";

export default function ContentPage() {
  const { user } = useContext(AuthContext);
  const [content, loading, error, refreshContent] = useContent({ user });
  // Where each content item is played (the regular broadcasts that hold it)
  const [usage, refreshBroadcasts] = useContentUsage();

  // Chosen values of each filter; an empty list means "any"
  const [filter, setFilter] = useState({ resolutions: [], used: [] })

  // Refresh reloads the broadcasts too: the green marks and the "Broadcasts" column come from them
  const refresh = () => Promise.all([refreshContent(), refreshBroadcasts()])

  // The same object for as long as the usage does not change, so the table keeps its search texts
  const tableContext = useMemo(() => ({ usage }), [usage])

  const resolutionOptions = useMemo(() => [
    ...resolutionCounts(content).map(item => ({ value: item.key, label: `${item.key} (${item.count})` })),
    { value: 'none', label: 'No resolution known' },
  ], [content])

  const data = useMemo(() => content.filter(item => {
    if (filter.resolutions.length > 0 && !filter.resolutions.includes(resolutionKey(item) || 'none')) return false
    if (filter.used.length > 0 && !filter.used.includes(usage.has(item.id) ? 'in' : 'out')) return false
    return true
  }), [content, filter, usage])

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} flex="1" minH={0} display="flex" flexDirection="column">
      <DataTableNew
        columns={columnsContent}
        data={data}
        totalCount={content.length}
        loading={loading}
        refresh={refresh}
        context={tableContext}
        resetPageKey={filter}
        defaultSort={[{ id: 'timestamp', desc: true }]}
        filters={(
          <>
          <MultiSelectFilter label='Any resolution' width='180px'
            options={resolutionOptions}
            value={filter.resolutions}
            onChange={resolutions => setFilter({ ...filter, resolutions })} />
          <MultiSelectFilter label='In broadcasts or not' width='230px'
            options={[
              { value: 'in', label: 'In a broadcast (green)' },
              { value: 'out', label: 'Not in any broadcast' },
            ]}
            value={filter.used}
            onChange={used => setFilter({ ...filter, used })} />
          </>
        )}
        enableSelection
        bulkActions={(rows, clearSelection) => (
          <ConfirmationDialog
            title='Delete content'
            message={`Are you sure you want to delete ${rows.length} item(s)? This cannot be undone.`}
            confirmLabel='Delete'
            trigger={
              <Button colorScheme='red' variant='solid' size='sm' borderRadius={30} px='6'>
                Bulk delete ({rows.length})
              </Button>
            }
            onConfirm={async () => {
              const results = await Promise.allSettled(rows.map(r => removeContent(r)))
              clearSelection()
              refresh()
              const failed = results.filter(r => r.status === 'rejected')
              if (failed.length > 0) {
                throw new Error(`${failed.length} item(s) could not be deleted (they may be used in broadcasts).`)
              }
            }}
          />
        )}
        actions={(
          <ActionDrawer
            title='Upload content'
            size='lg'
            trigger={
              <Button colorScheme='brand' variant='solid' size='sm' borderRadius={30} px='8'>
                Upload
              </Button>
            }
            >
            <ContentUploadModal onFinish={refresh} />
          </ActionDrawer>
        )}
      />
    </Box>
  );
}
